import React, { useEffect, useRef, useState } from "react";

const formatElapsed = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

/** Strip trailing dots/ellipsis from backend messages (CSS adds animated version) */
const stripTrailingDots = (msg) => (msg || "").replace(/\.{1,3}\s*$/, "");

/** Map internal stage_index (0-10) to user-facing phase */
const PHASE_CONFIG = [
  { key: "requirements", label: "Requirements", maxStage: 2 },
  { key: "search", label: "Search", maxStage: 4 },
  { key: "scoring", label: "Scoring", maxStage: 6 },
  { key: "outreach", label: "Outreach", maxStage: 10 },
];

const stageToPhaseIndex = (stageIndex) => {
  if (stageIndex <= 2) return 0;
  if (stageIndex <= 4) return 1;
  if (stageIndex <= 6) return 2;
  return 3;
};

const formatRemaining = (estimatedRemainingSecs, stageIndex, totalStages) => {
  if (estimatedRemainingSecs == null) return "";
  if (stageIndex >= totalStages - 2) return "Almost done...";
  if (estimatedRemainingSecs < 60) return "Less than a minute remaining";
  const mins = Math.ceil(estimatedRemainingSecs / 60);
  return `~${mins} min remaining`;
};

const Spinner = () => (
  <svg
    className="sourcing-spinner"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
  >
    <circle cx="7" cy="7" r="5.5" stroke="#E5E7EB" strokeWidth="2" />
    <path
      d="M12.5 7a5.5 5.5 0 00-5.5-5.5"
      stroke="#148EFF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const Checkmark = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="7" fill="#148EFF" />
    <path
      d="M4 7.2L6.1 9.3L10 5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CancelIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="7" fill="#F59E0B" />
    <path
      d="M5 5L9 9M9 5L5 9"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Pulsing activity dot */
const ActivityDot = () => <span className="sourcing-activity-dot" />;

/** Stage pill chip */
const StagePill = ({ label, state }) => {
  const base = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-all duration-300";
  if (state === "completed") {
    return (
      <span className={`${base} bg-blue-100 text-blue-600`}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2.5 5.2L4.2 6.9L7.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {label}
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className={`${base} border border-blue-400 text-blue-600 sourcing-pill-active`}>
        <span className="sourcing-activity-dot-sm" />
        {label}
      </span>
    );
  }
  return (
    <span className={`${base} border border-gray-200 text-gray-400`}>
      {label}
    </span>
  );
};

const SourcingProgress = ({ progress, completed, cancelled, isCancelling, onCancel }) => {
  const [localElapsed, setLocalElapsed] = useState(progress?.elapsedSecs || 0);
  const [expanded, setExpanded] = useState(true);
  const [displayedMessage, setDisplayedMessage] = useState("");
  const [isFading, setIsFading] = useState(false);
  const [countPop, setCountPop] = useState(false);
  const [interpolatedProgress, setInterpolatedProgress] = useState(0);
  const intervalRef = useRef(null);
  const prevCandidatesRef = useRef(0);
  const prevStageRef = useRef(0);
  const lastStageChangeRef = useRef(Date.now());
  const interpolationRef = useRef(null);
  const prevSessionActiveRef = useRef(false);

  // Reset all internal state when a new sourcing session starts
  useEffect(() => {
    const isActive = progress?.isActive && !completed && !cancelled;
    const wasInactive = !prevSessionActiveRef.current;
    if (isActive && wasInactive) {
      setLocalElapsed(progress?.elapsedSecs || 0);
      setDisplayedMessage("");
      setIsFading(false);
      setCountPop(false);
      setInterpolatedProgress(0);
      prevCandidatesRef.current = 0;
      prevStageRef.current = 0;
      lastStageChangeRef.current = Date.now();
    }
    prevSessionActiveRef.current = !!isActive;
  }, [progress?.isActive, completed, cancelled]);

  // Sync elapsed from backend
  useEffect(() => {
    if (progress?.elapsedSecs != null) {
      setLocalElapsed(progress.elapsedSecs);
    }
  }, [progress?.elapsedSecs]);

  // Local elapsed timer
  useEffect(() => {
    if (completed || cancelled || isCancelling) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setLocalElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [completed, cancelled, isCancelling]);

  // Message fade transition (3.2)
  const currentMessage = progress?.steps?.length
    ? progress.steps[progress.steps.length - 1]?.message
    : "";

  useEffect(() => {
    if (!currentMessage || currentMessage === displayedMessage) return;
    setIsFading(true);
    const timer = setTimeout(() => {
      setDisplayedMessage(currentMessage);
      setIsFading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [currentMessage, displayedMessage]);

  // Candidate count pop animation (3.3)
  const candidatesFound = progress?.candidatesFound ?? 0;
  useEffect(() => {
    if (candidatesFound > prevCandidatesRef.current) {
      setCountPop(true);
      const timer = setTimeout(() => setCountPop(false), 200);
      prevCandidatesRef.current = candidatesFound;
      return () => clearTimeout(timer);
    }
    prevCandidatesRef.current = candidatesFound;
  }, [candidatesFound]);

  // Track stage changes for interpolation
  const stageIndex = progress?.stageIndex || 0;
  const totalStages = progress?.totalStages || 0;

  useEffect(() => {
    if (stageIndex !== prevStageRef.current) {
      prevStageRef.current = stageIndex;
      lastStageChangeRef.current = Date.now();
    }
  }, [stageIndex]);

  // Sub-progress interpolation (3.5)
  useEffect(() => {
    if (completed || cancelled || totalStages === 0) {
      clearInterval(interpolationRef.current);
      return;
    }

    const baseProgress = totalStages > 0 ? stageIndex / totalStages : 0;
    setInterpolatedProgress(baseProgress);

    interpolationRef.current = setInterval(() => {
      const secsSinceLastUpdate = (Date.now() - lastStageChangeRef.current) / 1000;
      if (secsSinceLastUpdate > 30 && totalStages > 0) {
        const nextStageProgress = (stageIndex + 1) / totalStages;
        const maxInterpolation = baseProgress + (nextStageProgress - baseProgress) * 0.9;
        setInterpolatedProgress((prev) =>
          Math.min(prev + 0.0005, maxInterpolation)
        );
      }
    }, 10000);

    return () => clearInterval(interpolationRef.current);
  }, [stageIndex, totalStages, completed, cancelled]);

  const steps = progress?.steps || [];
  const completedSteps = steps.slice(0, -1);
  const currentStep = (completed || cancelled) ? null : steps[steps.length - 1];
  const currentPhaseIndex = stageToPhaseIndex(stageIndex);
  const timeEstimate = formatRemaining(progress?.estimatedRemainingSecs, stageIndex, totalStages);
  const progressPercent = Math.min(interpolatedProgress * 100, 100);

  // Cancelled summary
  if (cancelled) {
    return (
      <div className="flex items-center gap-2 py-1 text-[13px] text-gray-500">
        <CancelIcon />
        <span>
          Sourcing cancelled
          {candidatesFound > 0 &&
            ` — ${candidatesFound} candidate${candidatesFound !== 1 ? "s" : ""} saved`}
        </span>
        {localElapsed > 0 && (
          <span className="text-gray-400 ml-auto text-xs">
            {formatElapsed(localElapsed)}
          </span>
        )}
      </div>
    );
  }

  // Completed summary
  if (completed) {
    return (
      <div className="flex items-center gap-2 py-1 text-[13px] text-gray-500">
        <Checkmark />
        <span>
          Sourcing complete
          {candidatesFound > 0 &&
            ` — ${candidatesFound} candidate${candidatesFound !== 1 ? "s" : ""} found`}
        </span>
        {localElapsed > 0 && (
          <span className="text-gray-400 ml-auto text-xs">
            {formatElapsed(localElapsed)}
          </span>
        )}
      </div>
    );
  }

  // Cancelling in-progress state (1-10s wait for backend response)
  if (isCancelling) {
    return (
      <div className="text-[13px]">
        <div className="flex items-center gap-2 py-1">
          <Spinner />
          <span className="text-gray-500 font-medium">Cancelling sourcing</span>
          {candidatesFound > 0 && (
            <span className="text-gray-400 font-normal">
              · {candidatesFound} found so far
            </span>
          )}
          <span className="text-gray-400 ml-auto text-xs">
            {formatElapsed(localElapsed)}
          </span>
        </div>
        <div className="ml-[7px] border-l border-gray-200 pl-4 mt-1 space-y-2">
          <div className="flex items-center gap-2 py-0.5 text-gray-400">
            <span className="sourcing-activity-dot-amber" />
            <span className="sourcing-progress-message">
              Saving found candidates before stopping
            </span>
          </div>
          {/* Frozen progress bar */}
          {totalStages > 0 && (
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </div>
        <style>{`
          .sourcing-spinner {
            animation: sourcingSpin 0.8s linear infinite;
            flex-shrink: 0;
          }
          @keyframes sourcingSpin {
            to { transform: rotate(360deg); }
          }
          .sourcing-progress-message::after {
            content: '.';
            animation: sourcingEllipsis 1.5s steps(3, end) infinite;
          }
          @keyframes sourcingEllipsis {
            0%   { content: '.'; }
            33%  { content: '..'; }
            66%  { content: '...'; }
            100% { content: ''; }
          }
          .sourcing-activity-dot-amber {
            display: inline-block;
            width: 8px;
            height: 8px;
            min-width: 8px;
            border-radius: 50%;
            background-color: #f59e0b;
            animation: sourcingPulse 2s ease-in-out infinite;
          }
          @keyframes sourcingPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.5; transform: scale(0.85); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="text-[13px]">
      {/* Header row */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full text-left py-1 group"
      >
        <Spinner />
        <span className="text-gray-700 font-medium">
          Searching for candidates...
        </span>
        {candidatesFound > 0 && (
          <span
            className={`text-gray-400 font-normal transition-transform duration-200 ${
              countPop ? "sourcing-count-pop" : ""
            }`}
          >
            · <span className="font-semibold text-gray-600">{candidatesFound}</span> found
          </span>
        )}
        <span className="text-gray-400 ml-auto text-xs">
          {formatElapsed(localElapsed)}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Expandable detail */}
      {expanded && (
        <div className="ml-[7px] border-l border-gray-200 pl-4 mt-1 space-y-2">
          {/* Stage pills (4.1) */}
          {totalStages > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-1">
              {PHASE_CONFIG.map((phase, i) => (
                <StagePill
                  key={phase.key}
                  label={phase.label}
                  state={
                    i < currentPhaseIndex
                      ? "completed"
                      : i === currentPhaseIndex
                      ? "active"
                      : "pending"
                  }
                />
              ))}
            </div>
          )}

          {/* Completed steps */}
          {completedSteps.map((step, i) => (
            <div
              key={i}
              className="flex items-center gap-2 py-0.5 text-gray-400"
            >
              <Checkmark />
              <span>{step.message}</span>
            </div>
          ))}

          {/* Current active step with animated ellipsis (3.1) and fade (3.2) */}
          {currentStep && (
            <div className="flex items-center gap-2 py-0.5 text-gray-700">
              <ActivityDot />
              <span
                className={`sourcing-progress-message transition-opacity duration-200 ${
                  isFading ? "opacity-0" : "opacity-100"
                }`}
              >
                {stripTrailingDots(displayedMessage || currentStep.message)}
              </span>
            </div>
          )}

          {/* Progress bar with interpolation (3.5) */}
          {totalStages > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-2">
                  {candidatesFound > 0 && (
                    <span>
                      Candidates found:{" "}
                      <span
                        className={`font-semibold text-gray-600 inline-block transition-transform duration-200 ${
                          countPop ? "sourcing-count-pop" : ""
                        }`}
                      >
                        {candidatesFound}
                      </span>
                    </span>
                  )}
                </div>
                {timeEstimate && (
                  <span className="text-gray-400">{timeEstimate}</span>
                )}
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Cancel button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={onCancel}
              className="text-xs text-gray-500 hover:text-red-500 border border-gray-300 hover:border-red-300 rounded-md px-3 py-1 transition-colors"
            >
              Cancel Sourcing
            </button>
          </div>
        </div>
      )}

      <style>{`
        .sourcing-spinner {
          animation: sourcingSpin 0.8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes sourcingSpin {
          to { transform: rotate(360deg); }
        }

        /* 3.1 Animated ellipsis */
        .sourcing-progress-message::after {
          content: '.';
          animation: sourcingEllipsis 1.5s steps(3, end) infinite;
        }
        @keyframes sourcingEllipsis {
          0%   { content: '.'; }
          33%  { content: '..'; }
          66%  { content: '...'; }
          100% { content: ''; }
        }

        /* 3.3 Candidate count pop */
        .sourcing-count-pop {
          animation: sourcingPop 200ms ease-out;
        }
        @keyframes sourcingPop {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.15); }
          100% { transform: scale(1); }
        }

        /* 3.4 Pulsing activity dot */
        .sourcing-activity-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          min-width: 8px;
          border-radius: 50%;
          background-color: #3b82f6;
          animation: sourcingPulse 2s ease-in-out infinite;
        }
        .sourcing-activity-dot-sm {
          display: inline-block;
          width: 5px;
          height: 5px;
          min-width: 5px;
          border-radius: 50%;
          background-color: #3b82f6;
          animation: sourcingPulse 2s ease-in-out infinite;
        }
        @keyframes sourcingPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(0.85); }
        }

        /* Active pill pulse */
        .sourcing-pill-active {
          animation: sourcingPillPulse 2s ease-in-out infinite;
        }
        @keyframes sourcingPillPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.3); }
          50%      { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0); }
        }
      `}</style>
    </div>
  );
};

export default SourcingProgress;
